/**
 * Pied de page du site (liens, contact, newsletter)
 *
 * Rôle : Affiche logo, liens (CGV, mentions légales, etc.), coordonnées, réseaux sociaux et formulaire
 * d'inscription newsletter (côté client ; API newsletter non branchée).
 * Technologies : React, Next.js (Link, Image)
 */
"use client";

import Image from "next/image";
import Link from "next/link";
import { MapPin, Phone, Mail, Youtube, Instagram, Linkedin, Send } from "lucide-react";
import { useState, useEffect } from "react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!acceptedTerms || !email) return;

    setIsSubmitting(true);
    // TODO: Implémenter l'API newsletter
    setTimeout(() => {
      setIsSubmitting(false);
      alert("Merci pour votre inscription à la newsletter !");
      setEmail("");
      setAcceptedTerms(false);
    }, 1000);
  };

  // Ne pas rendre le footer côté serveur pour éviter les problèmes d'hydration
  if (!mounted) {
    return null;
  }

  return (
    <>
      <footer className="w-full mt-auto">
        <div className="mx-auto max-w-7xl px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            {/* Colonne 1 : Logo et Contact */}
            <div className="lg:col-span-1">
              <div className="mb-6">
                <div className="flex items-center gap-4 mb-4">
                  <Image
                    src="/logos/otulaa-rond.png"
                    alt="Otulaa Logo"
                    width={50}
                    height={50}
                    className="w-12 h-12"
                  />
                  <div>
                    <Image
                      src="/logos/otulaa-texte.png"
                      alt="Otulaa"
                      width={120}
                      height={30}
                      className="h-auto"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-3 text-gray-700">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm">
                    OTULAA<br />
                    170 Route de Font de Cine<br />
                    06220 Vallauris, France
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  <a href="tel:+33422329188" className="text-sm hover:text-orange-500 transition-colors">
                    04 22 32 91 88
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  <a href="mailto:otulaa@otulaa.com" className="text-sm hover:text-orange-500 transition-colors">
                    otulaa@otulaa.com
                  </a>
                </div>
              </div>
            </div>

            {/* Colonne 2 : Notre société */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Notre société</h3>
              <ul className="space-y-2">
                <li>
                  <Link href="/a-propos" className="text-sm text-gray-700 hover:text-orange-500 transition-colors">
                    À propos
                  </Link>
                </li>
                <li>
                  <Link href="/tarifs" className="text-sm text-gray-700 hover:text-orange-500 transition-colors">
                    Tarifs
                  </Link>
                </li>
                <li>
                  <Link href="/mentions-legales" className="text-sm text-gray-700 hover:text-orange-500 transition-colors">
                    Mentions légales
                  </Link>
                </li>
                <li>
                  <Link href="/cgv" className="text-sm text-gray-700 hover:text-orange-500 transition-colors">
                    Conditions Générales de Vente
                  </Link>
                </li>
                <li>
                  <Link href="/politique-de-confidentialite" className="text-sm text-gray-700 hover:text-orange-500 transition-colors">
                    Politique de Confidentialité
                  </Link>
                </li>
                <li>
                  <Link href="/contact" className="text-sm text-gray-700 hover:text-orange-500 transition-colors">
                    Contactez-nous
                  </Link>
                </li>
              </ul>
            </div>

            {/* Colonne 3 : Newsletter */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Abonnement à la newsletter</h3>
              <p className="text-sm text-gray-600 mb-4">
                Vous pouvez vous désinscrire à tout moment. Vous trouverez pour cela nos informations de contact dans les conditions d&apos;utilisation du site.
              </p>
              <form onSubmit={handleNewsletterSubmit} className="space-y-3">
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="newsletter-terms"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <label htmlFor="newsletter-terms" className="text-sm text-gray-700">
                    J&apos;accepte les{" "}
                    <Link href="/cgv" className="text-orange-500 hover:text-orange-600 underline" target="_blank" rel="noopener noreferrer">
                      conditions générales
                    </Link>
                    {" "}et la{" "}
                    <Link href="/politique-de-confidentialite" className="text-orange-500 hover:text-orange-600 underline" target="_blank" rel="noopener noreferrer">
                      politique de confidentialité
                    </Link>
                  </label>
                </div>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Votre adresse e-mail"
                    required
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                  />
                  <button
                    type="submit"
                    disabled={!acceptedTerms || !email || isSubmitting}
                    className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Réseaux sociaux et Copyright */}
          <div className="border-t border-gray-300 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-4">
                <a
                  href="https://www.youtube.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-red-600 transition-colors"
                  aria-label="YouTube"
                >
                  <Youtube className="w-6 h-6" />
                </a>
                <a
                  href="https://www.instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-pink-600 transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-6 h-6" />
                </a>
                <a
                  href="https://www.linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-6 h-6" />
                </a>
              </div>
              <p className="text-sm text-gray-600">
                © {new Date().getFullYear()} - OTULAA
              </p>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

